// FormsVaga.jsx
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { Form } from 'react-final-form';
import { useHistory, useParams } from 'react-router-dom';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import { notification } from '@spark/ui-components/utils/notification';
import { formStructure, formSections, validateForm } from './formConfig';
import { getVagaById, createVaga, updateVaga, getComplementares, getBeneficios } from '@spark/services/recrutamento-rh';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import IAITextarea from './IAITextarea';

const mapFormToApi = (values) => {
  const toIdArray = (v) => {
    if (!v) return [];
    if (Array.isArray(v)) {
      return v.map(x => {
        if (typeof x === 'object' && x !== null && 'value' in x) {
          return Number(x.value);
        }
        return Number(x);
      }).filter(n => Number.isFinite(n));
    }
    return [];
  };

  const formatDate = (d) => {
    if (!d) return null;
    if (d instanceof Date && !isNaN(d)) return d.toISOString();
    const tmp = new Date(d);
    return isNaN(tmp) ? null : tmp.toISOString();
  };

  const toDateTimeSql = (value) => {
    if (!value) return null;
    const raw = String(value).trim();
    if (!raw) return null;
    if (raw.includes('T')) {
      const [datePart, timePart = '00:00'] = raw.split('T');
      const time = timePart.slice(0, 5);
      return `${datePart} ${time}:00`;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      return `${raw} 00:00:00`;
    }
    return raw;
  };

  const toOneOrZero = (v) => {
    if (v === undefined || v === null || v === '') return null; // sem seleção
    if (typeof v === 'object' && v !== null && 'value' in v) return Number(v.value);
    return Number(v);
  };

  return {
    Titulo: values.Titulo?.trim() || '',
    Endereco: values.Endereco?.trim() || '',
    Descricao: values.Descricao?.trim() || '',
    DescricaoVaga: values.DescricaoVaga?.trim() || '',
    Salario: values.Salario !== undefined && values.Salario !== null && values.Salario !== '' ? String(values.Salario) : null,
    Ativo: 1,
    Data_abertura: new Date().toISOString(),
    NecessitaCNH: toOneOrZero(values.NecessitaCNH),
    EncerramentoAutomatico: toOneOrZero(values.EncerramentoAutomatico),
    DataEncerramentoAutomatico: toDateTimeSql(values.DataEncerramentoAutomatico),
    complementaresIds: toIdArray(values.complementaresIds),
    beneficiosIds: toIdArray(values.beneficiosIds),
  };
};

const mapApiToForm = (data) => {
  const parseDate = (iso) => (iso ? new Date(iso) : null);
  const toDateTimeLocal = (value) => {
    if (!value) return '';
    const raw = String(value).trim();
    if (!raw) return '';
    if (raw.includes('T')) return raw.slice(0, 16);
    if (raw.includes(' ')) return raw.replace(' ', 'T').slice(0, 16);
    return raw;
  };

  const cnhOption = (v) => {
    if (v === 1 || v === '1') return { value: '1', label: 'Sim' };
    if (v === 0 || v === '0') return { value: '0', label: 'Não' };
    return null;
  };

  const compSelected = Array.isArray(data?.complementares)
    ? data.complementares.map((c) => ({
      value: String(c.complementar_id ?? c.id),
      label: c.Nome,
      raw: c,
    }))
    : [];

  const benefSelected = Array.isArray(data?.beneficios)
    ? data.beneficios.map((b) => ({
      value: String(b.Beneficios_id ?? b.id),
      label: b.Nome,
      raw: b,
    }))
    : [];

  return {
    Titulo: data.Titulo || '',
    Endereco: data.Endereco || '',
    Descricao: data.Descricao || '',
    DescricaoVaga: data.DescricaoVaga || '',
    Salario: data.Salario || '',
    Ativo: String(data.Ativo ?? 1),
    Data_abertura: parseDate(data.Data_abertura),
    NecessitaCNH: cnhOption(data.NecessitaCNH),
    EncerramentoAutomatico: cnhOption(data.EncerramentoAutomatico),
    DataEncerramentoAutomatico: toDateTimeLocal(data.DataEncerramentoAutomatico),

    complementaresIds: compSelected,
    beneficiosIds: benefSelected,

    complementaresLista: Array.isArray(data?.complementares)
      ? data.complementares.map((c) => c.Nome)
      : [],
    beneficiosLista: Array.isArray(data?.beneficios)
      ? data.beneficios.map((b) => b.Nome)
      : [],
  };
};

const isEncerramentoAutomaticoAtivo = (value) => {
  if (value === undefined || value === null || value === '') return false;
  if (typeof value === 'object' && value !== null && 'value' in value) {
    return String(value.value) === '1';
  }
  return String(value) === '1';
};

const applyEncerramentoAutomaticoState = (structure, values, isReadOnly) => {
  const encerramentoAtivo = isEncerramentoAutomaticoAtivo(values?.EncerramentoAutomatico);
  return (structure || []).map((row) =>
    (row || []).map((field) => {
      if (!field || field.name !== 'DataEncerramentoAutomatico') return field;
      return {
        ...field,
        disabled: isReadOnly || !encerramentoAtivo,
      };
    })
  );
};

const Forms = ({ isReadOnly = false }) => {
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [compOptions, setCompOptions] = useState([]);
  const [benefOptions, setBenefOptions] = useState([]);
  const menuPortalTarget = typeof window !== 'undefined' ? document.body : null;
  const selectMenuStyles = useMemo(() => ({menuPortal: (base) => ({ ...base, zIndex: 9999 })}), []);

  const isEditMode = !!id && id !== 'criar' && !isReadOnly;
  const isDetailMode = !!id && id !== 'criar' && isReadOnly;

  useEffect(() => {
    const fetchComplementares = async () => {
      try {
        const compl = await getComplementares();
        const lista = Array.isArray(compl?.registros) ? compl.registros : Array.isArray(compl) ? compl : [];

        const opts = lista.map((r) => {
          if (r && typeof r === 'object' && 'value' in r && 'label' in r) {
            return r;
          }
          return {
            value: Number(r.id),
            label: r.Nome,
            raw: r,
          };
        });

        setCompOptions(opts);
      } catch (e) {

      }
    };

    fetchComplementares();
  }, []);

  useEffect(() => {
    const fetchBeneficios = async () => {
      try {
        const resp = await getBeneficios();
        const lista = Array.isArray(resp?.registros) ? resp.registros : Array.isArray(resp) ? resp : [];
        const opts = lista.map(b => ({
          value: String(b.id),
          label: b.Nome,
          raw: b,
        }));
        setBenefOptions(opts);
      } catch (e) {

      }
    };
    fetchBeneficios();
  }, []);

  useEffect(() => {
    if ((isReadOnly || isEditMode) && id && id !== 'criar') {
      const load = async () => {
        try {
          setLoadingData(true);
          const data = await getVagaById(id);
          setInitialData(mapApiToForm(data));
        } catch (err) {
          console.error('Erro ao carregar vaga:', err);
        } finally {
          setLoadingData(false);
        }
      };
      load();
    }
  }, [isReadOnly, isEditMode, id]);

  useEffect(() => {
    if (!initialData || !Array.isArray(initialData.complementares)) return;
    if (Array.isArray(initialData.complementaresIds) && initialData.complementaresIds.length > 0) return;

    const fromApi = initialData.complementares.map(c => ({
      value: String(c.complementar_id ?? c.id),
      label: c.Nome,
      raw: c,
    }));

    setInitialData(prev => {
      if (Array.isArray(prev?.complementaresIds) && prev.complementaresIds.length > 0) return prev;
      return { ...prev, complementaresIds: fromApi };
    });
  }, [initialData?.complementares]);

  const structureWithSelects = useMemo(() => {
    return formStructure.map(row =>
      row.map(field => {
        if (field.name === 'DescricaoVaga') {
          return {
            ...field,
            component: IAITextarea,
            type: 'text',
            rows: field.rows ?? 2,
            label: field.label || 'Descrição da Vaga (detalhada)',
            placeholder: field.placeholder || 'Detalhes, responsabilidades…',
          };
        }
        if (field.name === 'complementaresIds') {
          return {
            ...field,
            component: 'SearchSelect',
            isMulti: true,
            options: compOptions,
            placeholder: field.placeholder || 'Selecione...',
            menuPortalTarget: menuPortalTarget || undefined,
            menuPosition: 'fixed',
            styles: selectMenuStyles,
          };
        }
        if (field.name === 'beneficiosIds') {
          return {
            ...field,
            component: 'SearchSelect',
            isMulti: true,
            options: benefOptions,
            placeholder: field.placeholder || 'Selecione...',
            menuPortalTarget: menuPortalTarget || undefined,
            menuPosition: 'fixed',
            styles: selectMenuStyles,
          };
        }

        return field;
      })
    );
  }, [compOptions, benefOptions, isReadOnly, menuPortalTarget, selectMenuStyles]);

  const onSubmit = useCallback(
    async (values) => {

      try {
        setLoading(true);
        const payload = mapFormToApi(values);
        if (isEditMode == true) {
          await updateVaga(id, payload);
          notification.success('Vaga atualizada com sucesso!');
        } else {
          await createVaga(payload);
          notification.success('Vaga criada com sucesso!');
        }

        history.goBack();
      } catch (err) {
        notification.error('Erro ao salvar a vaga. Tente novamente.');
      } finally {
        setLoading(false);
      }
    },
    [history, id, isEditMode]
  );

  const defaultValues = useMemo(
    () => ({
      Titulo: '',
      Endereco: '',
      Descricao: '',
      DescricaoVaga: '',
      Salario: '',
      Ativo: '1',
      Data_abertura: null,
      NecessitaCNH: null,
      EncerramentoAutomatico: null,
      DataEncerramentoAutomatico: '',
      complementaresIds: [],
    }),
    []
  );

  const handleSearchComplementares = useCallback(async (term) => {
    const t = (term || '').toLowerCase();
    return (compOptions || []).filter(o =>
      o.label.toLowerCase().includes(t)
    );
  }, [compOptions]);

  const handleSearchBeneficios = useCallback(async (term) => {
    const t = (term || '').toLowerCase();
    return benefOptions.filter(o => o.label.toLowerCase().includes(t));
  }, [benefOptions]);

  const structureForRender = useMemo(() => {
    const base = structureWithSelects ?? formStructure;

    if (!isDetailMode) return base;

    return base.map((row) =>
      row.filter(
        (field) => field.name !== 'complementaresIds' && field.name !== 'beneficiosIds'
      )
    );
  }, [isDetailMode, structureWithSelects, formStructure]);

  const initialValues = initialData || defaultValues;

  if ((isReadOnly || isEditMode) && loadingData) {
    return (
      <Card>
        <CardBody>
          <div style={{ textAlign: 'center', padding: 50 }}>Carregando dados da vaga...</div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Form onSubmit={onSubmit} initialValues={initialValues} validate={validateForm}>
      {({ handleSubmit, values }) => {
        const structureWithDisable = applyEncerramentoAutomaticoState(
          structureForRender,
          values || initialValues,
          isReadOnly
        );
        return (
          <Card>
            <CardBody>
              <FormContainer onSubmit={handleSubmit}>
                <DynamicForm
                  structure={structureWithDisable}
                  sections={formSections(initialValues)}
                  isViewMode={isReadOnly}
                  isEditing={isEditMode}
                  searchFunctions={{
                    complementaresIds: handleSearchComplementares,
                    beneficiosIds: handleSearchBeneficios,
                  }}
                />

                <div className="row" style={{ marginTop: 12, width: '100vw', backgroundColor: '#f5f5f5', padding: 15, }}>
                  <div className="col-md-12">
                    <IAITextarea
                      name="DescricaoVaga"
                      label="Descrição da Vaga (detalhada)"
                      placeholder="Detalhes, responsabilidades…"
                      type="text"
                      rows={5}
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                    />
                  </div>
                </div>

                {isDetailMode && (
                  <div style={{ marginTop: 24 }}>
                    <div style={{ marginBottom: 16 }}>
                      <strong>Informações Complementares:</strong>
                      {Array.isArray(initialValues?.complementaresLista) &&
                        initialValues.complementaresLista.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                          {initialValues.complementaresLista.map((nome, idx) => (
                            <span
                              key={`comp-${idx}`}
                              style={{
                                padding: '6px 10px',
                                background: '#F2F6FF',
                                border: '1px solid #D9E4FF',
                                borderRadius: 999,
                                fontSize: 13,
                              }}
                            >
                              {nome}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div style={{ marginTop: 8, color: '#666' }}>
                          Nenhum complementar vinculado.
                        </div>
                      )}
                    </div>

                    <div>
                      <strong>Benefícios:</strong>
                      {Array.isArray(initialValues?.beneficiosLista) &&
                        initialValues.beneficiosLista.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                          {initialValues.beneficiosLista.map((nome, idx) => (
                            <span
                              key={`benef-${idx}`}
                              style={{
                                padding: '6px 10px',
                                background: '#F7FFF2',
                                border: '1px solid #E1F8D2',
                                borderRadius: 999,
                                fontSize: 13,
                              }}
                            >
                              {nome}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div style={{ marginTop: 8, color: '#666' }}>
                          Nenhum benefício vinculado.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!isReadOnly && (
                  <FormButtonToolbar className="mt-5">
                    <ButtonWithLoading
                      variant="primary"
                      type="submit"
                      loading={loading ? 'true' : undefined}
                      disabled={loading}
                    >
                      {isEditMode ? 'Atualizar' : 'Salvar'}
                    </ButtonWithLoading>

                    <Button
                      variant="secondary"
                      type="button"
                      onClick={() => history.goBack()}
                    >
                      Cancelar
                    </Button>
                  </FormButtonToolbar>
                )}
              </FormContainer>
            </CardBody>
          </Card>
        );
      }}
    </Form>
  );

};

export default Forms;
